/* The address of the material, thrown on the wall.

   A room reads the address off the projector and types it into a phone. This
   puts it there as a code to scan and as text large enough to read from the
   back, and it is reachable from anywhere — the deck included, where the header
   that carries the button is hidden, which is why the `q` key exists.

   Three things it inherits from the static portal, each for a reason that has
   not changed:

     the code is computed, not stored — see lib/qr.ts;
     the code keeps a white plane under it, because the paper background is the
     first thing a phone camera gives up on;
     the address never wraps. It is read aloud and typed in from a distance, so
     the type size gives way, not the string.

   What has changed is where the address comes from. The old portal wrote it
   into a file that had to be edited when the domain moved; this asks the
   browser, which cannot be out of date. The language the room is being shown is
   part of it: an English audience should not have to find the switch. */

import { useCallback, useEffect, useRef } from "react";
import { useI18n } from "../lib/i18n";
import { useFeature } from "../lib/portal";
import { qrSvg } from "../lib/qr";

/* Where the room should land: this portal's home, in the language on screen. */
function address(home: string): string {
  return new URL(home, window.location.origin).href.replace(/\/$/, "");
}

export function QrOverlay({ open, onOpen, onClose }: {
  open: boolean; onOpen: () => void; onClose: () => void;
}) {
  const { ui, href } = useI18n();
  const on = useFeature("qr");
  const home = href("/");
  const close = useRef<HTMLButtonElement>(null);

  /* The focus follows the overlay in: a keyboard is then one Enter from
     closing it, and a screen reader is told where it suddenly is. Without
     this the focus stayed behind the dialog, on whatever opened it. */
  useEffect(() => {
    if (open && on) close.current?.focus();
  }, [on, open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      /* The file maker put the first text field into the material: a bare letter
         is a shortcut everywhere except inside one. */
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      const typing = /^(INPUT|TEXTAREA)$/.test(tag);
      /* Cmd+Q still quits the browser. */
      const plainQ = !typing && (e.key === "q" || e.key === "Q")
        && !e.metaKey && !e.ctrlKey && !e.altKey;

      if (open) {
        if (e.key === "Escape" || plainQ) onClose();
        /* Tab still moves the focus to the close button; everything else is
           held. While the address is on the wall the deck behind it must not
           advance, which is what a stray arrow key would otherwise do. */
        else if (e.key === "Tab" || e.metaKey || e.ctrlKey || e.altKey) return;
      } else if (plainQ && on) {
        onOpen();
      } else {
        return;
      }
      /* Captured on the way down and stopped dead here: in the deck Escape
         leaves the deck, and closing the overlay must not also close the slide.
         Immediate, because a listener on the same node as this one would
         otherwise still run — plain stopPropagation only stops the next node. */
      e.preventDefault();
      e.stopImmediatePropagation();
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [on, onClose, onOpen, open]);

  if (!open || !on) return null;

  const url = address(home);
  let code: string;
  try {
    code = qrSvg(url, { label: ui("qr.label") });
  } catch {
    code = "";
  }

  return (
    /* Anywhere on the overlay closes it: in front of a room the fastest way out
       has to be the one nobody has to aim for. */
    <div className="qrov" onClick={onClose}
         role="dialog" aria-modal="true" aria-label={ui("qr.button")}>
      <button type="button" ref={close} className="qrclose"
              aria-label={ui("qr.close")}>&times;</button>
      <div className="qrin">
        <div className="qrpane">
          {code
            ? <div dangerouslySetInnerHTML={{ __html: code }} />
            : <p className="qrfail">{url}</p>}
        </div>
        <div className="qrtext">
          <div className="qreyebrow">{ui("qr.eyebrow")}</div>
          <div className="qraddr">{url.replace(/^https?:\/\//, "")}</div>
        </div>
      </div>
    </div>
  );
}

/* The button in the header. It is furniture outside a room, so it goes with the
   switch that says whether a room is in front of the portal. */
export function QrButton({ onClick }: { onClick: () => void }) {
  const { ui } = useI18n();
  const on = useFeature("qr");
  const click = useCallback(() => onClick(), [onClick]);
  if (!on) return null;
  return (
    <button type="button" id="qrbtn" onClick={click}>{ui("qr.button")}</button>
  );
}
